import { bind } from 'bind-event-listener';
import { type Environment, fetchQuery, graphql } from 'react-relay';

import type { searchMentionAgentsQuery$data } from './__generated__/searchMentionAgentsQuery.graphql.ts';
import { mapEdgeToAgent } from './map-edge-to-agent.ts';
import type { MentionAgent } from './types.ts';

const TYPED_SEARCH_RESULT_LIMIT = 10;

export type SearchMentionAgentsParams = {
	environment: Environment;
	cloudId: string;
	product: string;
	workspaceId?: string;
	name: string;
	count?: number;
	signal?: AbortSignal;
};

/**
 * Fires the typed-search agent query. Uses `fetchQuery(...).subscribe()`
 * so the request can be cancelled via the `AbortSignal`. Aborts resolve
 * to an empty array rather than rejecting.
 */
export const searchMentionAgents = (
	params: SearchMentionAgentsParams,
): Promise<readonly MentionAgent[]> =>
	new Promise<readonly MentionAgent[]>((resolve, reject) => {
		if (params.signal?.aborted) {
			resolve([]);
			return;
		}

		let unbindAbortListener: (() => void) | null = null;
		const settle = (result: readonly MentionAgent[]) => {
			unbindAbortListener?.();
			resolve(result);
		};
		const fail = (error: Error) => {
			unbindAbortListener?.();
			reject(error);
		};

		const subscription = fetchQuery(
			params.environment,
			graphql`
				query searchMentionAgentsQuery(
					$cloudId: String!
					$product: String
					$workspaceId: String
					$name: String!
					$first: Int!
				) {
					agentStudio_getAgents(
						cloudId: $cloudId
						product: $product
						workspaceId: $workspaceId
						first: $first
						input: { name: $name }
					) @optIn(to: "AgentStudio") {
						edges {
							node {
								...mapEdgeToAgent
							}
						}
					}
				}
			`,
			{
				cloudId: params.cloudId,
				product: params.product,
				workspaceId: params.workspaceId,
				name: params.name,
				first: params.count ?? TYPED_SEARCH_RESULT_LIMIT,
			},
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (response) => {
				const data = response as searchMentionAgentsQuery$data | null | undefined;
				const edges = data?.agentStudio_getAgents?.edges ?? [];
				const result: MentionAgent[] = [];
				for (const edge of edges) {
					const mapped = mapEdgeToAgent(edge?.node);
					if (mapped) {
						result.push(mapped);
					}
				}
				settle(result);
			},
			error: fail,
		});

		if (params.signal) {
			unbindAbortListener = bind(params.signal, {
				type: 'abort',
				listener: () => {
					subscription.unsubscribe();
					settle([]);
				},
				options: { once: true },
			});
		}
	});
